import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiClock,
  FiCreditCard,
  FiExternalLink,
  FiFileText,
  FiRefreshCw,
  FiCheckCircle,
  FiAlertCircle,
} from 'react-icons/fi';
import { portalAPI } from '../../services/api';
import { usePortalAuth } from '../../context/PortalAuthContext';

const PortalPaymentsPage = () => {
  const { user } = usePortalAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dueLoading, setDueLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [tab, setTab] = useState('due');
  const [leasePayments, setLeasePayments] = useState([]);
  const [payments, setPayments] = useState([]);
  const [checkoutId, setCheckoutId] = useState(null);

  const role = user?.role_slug || user?.role || '';
  const isLocataire = role === 'locataire';

  const fetchData = async () => {
    setLoading(true);
    try {
      setDueLoading(true);
      const dueP = isLocataire
        ? portalAPI.billing.listLeasePayments({ status: 'pending' })
        : Promise.resolve({ data: [] });
      let histP = Promise.resolve({ data: [] });
      if (isLocataire) histP = portalAPI.billing.listPayments();
      const [dueR, histR] = await Promise.all([dueP, histP]);
      setLeasePayments(dueR.data || []);
      setPayments(histR.data || []);
    } catch (e) {
      setLeasePayments([]); setPayments([]);
    } finally {
      setDueLoading(false); setHistoryLoading(false); setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const summaryDue = useMemo(() => ({
    count: leasePayments.length,
    amount: leasePayments.reduce((s, i) => s + Number(i.amount||0) + Number(i.late_fee||0), 0),
    overdue: leasePayments.filter(i => i.due_date && new Date(i.due_date) < new Date() && i.status === 'pending').length
  }), [leasePayments]);

  const summaryHist = useMemo(() => ({
    count: payments.length,
    amount: payments.reduce((s, i) => s + Number(i.amount||0), 0),
    paid: payments.filter(i => i.status === 'completed' || i.status === 'paid').length
  }), [payments]);

  const fmtDate = v => v ? new Date(v).toLocaleDateString('fr-FR') : '-';
  const fmtAmt = v => Number(v||0).toLocaleString('fr-FR') + ' XOF';
  const handleCheckout = p => navigate(`/portal/lease-payments/${p.id}/checkout`);

  return (
    <div className="portal-payments-page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2>Mes Paiements</h2>
        <div className="tabs" style={{ display: 'flex', gap: 10 }}>
          <button 
            className={`btn ${tab === 'due' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setTab('due')}
          >
            À Payer ({summaryDue.count})
          </button>
          <button 
            className={`btn ${tab === 'history' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setTab('history')}
          >
            Historique ({summaryHist.count})
          </button>
        </div>
      </div>
      
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><FiRefreshCw className="spin" /> Chargement...</div>
      ) : tab === 'due' ? (
        <div className="payments-list" style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
          {leasePayments.length === 0 ? (
            <p>Aucun paiement en attente.</p>
          ) : (
            leasePayments.map(p => (
              <div key={p.id} className="card" style={{ padding: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 'bold' }}>{fmtAmt(p.amount)}</div>
                  <div style={{ color: '#666', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <FiClock /> Échéance: {fmtDate(p.due_date)}
                  </div>
                </div>
                <button className="btn btn-primary" onClick={() => handleCheckout(p)}>
                  Payer <FiExternalLink />
                </button>
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="payments-list" style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
          {payments.length === 0 ? (
            <p>Aucun historique de paiement.</p>
          ) : (
            payments.map(p => (
              <div key={p.id} className="card" style={{ padding: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 'bold' }}>{fmtAmt(p.amount)}</div>
                  <div style={{ color: '#666', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <FiFileText /> Date: {fmtDate(p.created_at || p.payment_date)}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  {p.status === 'completed' || p.status === 'paid' ? <FiCheckCircle color="green" /> : <FiAlertCircle color="orange" />}
                  <span style={{ textTransform: 'capitalize' }}>{p.status}</span>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default PortalPaymentsPage;
